import { useState, useEffect, useCallback } from 'react';
import { getMessaging, getToken, deleteToken, isSupported } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, getDocs, collection, serverTimestamp } from 'firebase/firestore';
import app, { db } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';

// ─── WEB PUSH ────────────────────────────────────────────────────────────────
//
// Notifications that arrive when Tunify is CLOSED. Distinct from the in-app
// system in NotificationContext, which needs the app open and a live Firestore
// listener; this one goes through FCM and the operating system, so it reaches
// the lock screen.
//
// SETTING UP THE VAPID KEY (one-off):
//   Firebase Console → Project settings → Cloud Messaging
//   → Web configuration → Web Push certificates → Generate key pair
//   Copy the key pair value into .env as:
//       REACT_APP_FIREBASE_VAPID_KEY=B...
//   Restart the dev server afterwards — CRA inlines REACT_APP_* at BOOT, so an
//   edited .env does nothing until the server is restarted.
//
// iOS 16.4+ ONLY, AND ONLY WHEN INSTALLED. Safari supports web push, but only
// for a site added to the Home Screen — in a normal tab there is no push at
// all, and requesting permission there fails. That is why `supported` below
// reports standalone separately: the UI needs to tell an iPhone user to install
// first rather than showing them a button that cannot work.

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// The Firebase values the service worker needs, handed over on the registration
// URL — a worker cannot read the app's build-time env. None are secret; they
// all ship in the client bundle already.
function swUrl() {
  const cfg = app.options;
  const q = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    authDomain: cfg.authDomain || '',
    projectId: cfg.projectId || '',
    storageBucket: cfg.storageBucket || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  });
  return `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js?${q}`;
}

const isStandalone = () =>
  typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );

const isIOS = () =>
  typeof navigator !== 'undefined' && (
    /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (/mac/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );

// ── ONE ROW PER DEVICE ───────────────────────────────────────────────────────
//
// Token rows are keyed by the token, which is right for pruning but means a
// device that has been issued more than one token over its life owns more than
// one row. Tokens rotate, and a freshly rotated one does not immediately kill
// its predecessor — so both rows can be live at once, sendEachForMulticast
// delivers to both, and the phone shows every message TWICE.
//
// A device id that outlives the token is what lets the stale rows be found and
// removed. Stored in localStorage: it identifies a browser profile to itself
// and nothing more — no fingerprinting, and clearing site data simply mints a
// new one.
const DEVICE_KEY = 'tunify.deviceId';

function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() || `d${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    // Private mode: no stable id available, so fall back to the user agent —
    // the legacy match below still collapses duplicates from the same browser.
    return '';
  }
}

/**
 * Writes this device's token and removes any OTHER row belonging to it.
 *
 * Shared by the switch and the refresh-on-start effect, which must agree
 * exactly — two slightly different writes is how the duplicates appeared in
 * the first place.
 */
async function saveDeviceToken(uid, token) {
  const ua = navigator.userAgent.slice(0, 200);
  const id = deviceId();

  await setDoc(doc(db, 'users', uid, 'fcmTokens', token), {
    token,
    deviceId: id,
    // Enough to tell devices apart in the console; no fingerprinting.
    userAgent: ua,
    platform: isIOS() ? 'ios' : 'web',
    standalone: isStandalone(),
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  }, { merge: true });

  // Sweep this device's older tokens. Rows written before deviceId existed are
  // matched on the user agent instead, so the duplicates already out there get
  // cleaned up on the next start rather than needing a manual purge.
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'fcmTokens'));
    const stale = snap.docs.filter((d) => {
      if (d.id === token) return false;                 // the row just written
      const row = d.data() || {};
      if (row.deviceId) return !!id && row.deviceId === id;
      return row.userAgent === ua;                      // legacy row, same browser
    });
    await Promise.all(stale.map((d) => deleteDoc(d.ref)));
  } catch {
    // A failed sweep costs a duplicate notification, not a broken feature.
  }
}

/**
 * Registers the messaging service worker. Exported so index.js can register it
 * at startup — the worker also carries the GLB cache, so it must come up
 * whether or not this user ever enables push.
 */
export function registerMessagingSW() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  return navigator.serviceWorker.register(swUrl(), { scope: '/' }).catch((err) => {
    console.warn('[push] service worker registration failed:', err);
    return null;
  });
}

export function usePushNotifications() {
  const { user } = useAuth();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  // The last outcome, in words, for the Settings card to show. Push fails
  // silently in a dozen ways — no VAPID key, a denied permission, a blocked
  // Firestore write, Safari in a tab — and each one looks identical from the
  // outside: the switch turns on and no notification ever arrives.
  const [status, setStatus] = useState(null);   // { ok, message, token? }

  // On iOS this is only true once the app has been added to the Home Screen,
  // which is why it is checked rather than assumed.
  const needsInstallFirst = isIOS() && !isStandalone();

  useEffect(() => {
    let alive = true;
    isSupported()
      .then((ok) => { if (alive) setSupported(ok && !!VAPID_KEY); })
      .catch(() => { if (alive) setSupported(false); });
    return () => { alive = false; };
  }, []);

  // ── RE-REGISTER THIS DEVICE ON EVERY START ─────────────────────────────────
  //
  // FCM tokens are not permanent. They rotate — on a browser update, after a
  // long idle, when storage is evicted — and the old one stops delivering.
  //
  // Registration used to happen ONLY when the switch was flipped, so once a
  // token rotated nothing ever wrote the new one: the switch still read "on",
  // Firestore still held the dead token, the trigger still ran, and the phone
  // went quiet with nothing anywhere reporting a failure. Push worked for a
  // while and then silently stopped, permanently.
  //
  // Re-fetching on load is cheap (FCM hands back the cached token when it is
  // still valid) and makes registration self-healing. Silent by design: no
  // permission is requested here, so it can only ever refresh a device the
  // user already opted in on.
  useEffect(() => {
    if (!user?.uid || !VAPID_KEY) return undefined;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return undefined;
    if (needsInstallFirst) return undefined;

    let alive = true;
    (async () => {
      try {
        const registration = await registerMessagingSW();
        if (!registration) return;
        await navigator.serviceWorker.ready;
        const fresh = await getToken(getMessaging(app), {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (!fresh || !alive) return;

        await saveDeviceToken(user.uid, fresh);
        if (alive) setToken(fresh);
      } catch {
        // Best-effort. A failure here is not worth interrupting anyone over —
        // the switch in the menu reports properly when it is used directly.
      }
    })();
    return () => { alive = false; };
  }, [user?.uid, needsInstallFirst]);

  /**
   * Asks for permission, gets the device token, and stores it.
   *
   * Must be called from a user gesture: browsers reject a permission prompt
   * that was not asked for, and Safari in particular will hard-deny it.
   */
  const report = useCallback((ok, message, tok) => {
    setStatus({ ok, message, token: tok || null });
    return { ok, message, token: tok || null };
  }, []);

  const enable = useCallback(async () => {
    if (!user?.uid || busy) return report(false, 'You are not signed in.');
    if (!VAPID_KEY) {
      return report(false, 'Missing VAPID key. Set REACT_APP_FIREBASE_VAPID_KEY in the build environment (Render → Environment, not just your local .env) and redeploy.');
    }
    if (needsInstallFirst) {
      return report(false, 'On iPhone, add Tunify to the Home Screen first — Safari gives no push token to a browser tab.');
    }

    setBusy(true);
    try {
      // NEVER RE-ASK FOR A PERMISSION WE ALREADY HAVE.
      //
      // requestPermission() is only honoured inside a user gesture. The
      // Settings switch awaits setBrowserPush() before calling enable(), and
      // that await ends the gesture — so a second request from here arrives
      // gesture-less and the browser answers `denied` outright, even though
      // the permission is sitting at `granted`. That denial then aborted the
      // whole flow before getToken() ever ran, which is why no token was ever
      // written.
      //
      // Asking is only ever needed from `default`. If it is already granted we
      // have what we came for; if it is already denied, re-asking cannot undo
      // that — only the browser's own site settings can.
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        return report(false, `Permission ${perm}. Allow notifications for this site in your browser settings.`);
      }

      // WAIT FOR THE WORKER TO BE ACTIVE, not merely registered.
      //
      // register() resolves as soon as the registration exists — the worker
      // may still be installing. getToken() against a registration with no
      // active worker fails, and this is the likeliest reason no token was
      // ever stored: the very first time push is switched on is exactly when
      // the worker is still starting up.
      const registration = await registerMessagingSW();
      if (!registration) {
        return report(false, 'Service worker did not register. Push needs HTTPS (or localhost) and a reachable /firebase-messaging-sw.js.');
      }
      await navigator.serviceWorker.ready;

      const messaging = getMessaging(app);
      let fcmToken;
      try {
        fcmToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
      } catch (err) {
        return report(false, `getToken failed — ${err?.code || ''} ${err?.message || err}`);
      }
      if (!fcmToken) {
        return report(false, 'Firebase returned no token. Check the VAPID key belongs to THIS Firebase project.');
      }

      try {
        await saveDeviceToken(user.uid, fcmToken);
      } catch (err) {
        // permission-denied here is the Firestore RULE rejecting the write —
        // a different failure from the browser permission above, and easy to
        // confuse with it.
        return report(false, `Firestore write failed — ${err?.code || ''} ${err?.message || err}`);
      }

      setToken(fcmToken);
      return report(true, 'Push enabled on this device.', fcmToken);
    } catch (err) {
      return report(false, `${err?.code || 'Error'}: ${err?.message || 'could not enable push'}`);
    } finally {
      setBusy(false);
    }
  }, [user?.uid, busy, needsInstallFirst, report]);

  /**
   * Stops push for THIS device.
   *
   * Deletes the token at the source as well as the Firestore row — dropping
   * only the row would leave FCM still delivering to a device we have simply
   * stopped recording.
   */
  const disable = useCallback(async () => {
    if (!user?.uid) return;
    setBusy(true);
    try {
      const messaging = getMessaging(app);
      const current = token || await getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
      if (current) {
        await deleteDoc(doc(db, 'users', user.uid, 'fcmTokens', current)).catch(() => {});
        await deleteToken(messaging).catch(() => {});
      }
      setToken(null);
    } finally {
      setBusy(false);
    }
  }, [user?.uid, token]);

  return {
    supported,
    permission,
    status,
    clearStatus: () => setStatus(null),
    token,
    busy,
    needsInstallFirst,
    enable,
    disable,
  };
}
